import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  KatexPreviewCard,
  KatexPreviewCardPanel
} from '@bayesprep/shared-ui/katex/components';

interface KatexPreviewProps {
  text: string;
  title?: string;
  className?: string;
  placeholder?: string;
}

export const KatexPreview: React.FC<KatexPreviewProps> = (props) => {
  return (
    <KatexPreviewCard
      {...props}
      portal="ADMIN"
      CardComponent={Card}
      CardHeaderComponent={CardHeader}
      CardContentComponent={CardContent}
      CardTitleComponent={CardTitle}
    />
  );
};

interface KatexPreviewPanelProps {
  questionText: string;
  questionType?: string;
  options: Array<{ text: string; isCorrect: boolean }>;
  numericalAnswer?: string;
  essayPrompt?: string;
  essayMaxWords?: number;
  matchingPairs?: Array<{ id: number; left: string; right: string }>;
  explanation: string;
  className?: string;
}

export const KatexPreviewPanel: React.FC<KatexPreviewPanelProps> = ({
  questionText,
  questionType,
  options,
  numericalAnswer,
  essayPrompt,
  essayMaxWords,
  matchingPairs,
  explanation,
  className
}) => {
  // For basic question types, use the shared component
  if (!questionType || ['MCQ_SINGLE_CORRECT', 'MCQ_MULTIPLE_CORRECT', 'TRUE_FALSE', 'MULTIPLE_CHOICE'].includes(questionType)) {
    return (
      <KatexPreviewCardPanel
        questionText={questionText}
        options={options}
        explanation={explanation}
        className={className}
        portal="ADMIN"
        CardComponent={Card}
        CardHeaderComponent={CardHeader}
        CardContentComponent={CardContent}
        CardTitleComponent={CardTitle}
      />
    );
  }

  // Custom preview for new question types
  return (
    <div className={`space-y-4 ${className || ''}`}>
      {/* Question Preview */}
      <KatexPreviewCard
        text={questionText}
        title="Question Preview"
        placeholder="Question text preview will appear here..."
        portal="ADMIN"
        CardComponent={Card}
        CardHeaderComponent={CardHeader}
        CardContentComponent={CardContent}
        CardTitleComponent={CardTitle}
      />

      {/* Question Type Specific Preview */}
      {questionType === 'FILL_IN_THE_BLANK_NUMERIC' && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Answer Preview</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="p-3 bg-blue-50 border border-blue-200 rounded">
              <div className="text-sm">
                <span className="font-medium">Numerical Answer: </span>
                {numericalAnswer || <span className="text-muted-foreground italic">No answer provided</span>}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {questionType === 'ESSAY' && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Essay Configuration</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-3">
              <div className="p-3 bg-purple-50 border border-purple-200 rounded">
                <div className="text-sm">
                  <span className="font-medium">Essay Prompt: </span>
                  {essayPrompt ? (
                    <div className="mt-1 text-slate-700">{essayPrompt}</div>
                  ) : (
                    <span className="text-muted-foreground italic">No prompt provided</span>
                  )}
                </div>
              </div>
              <div className="p-3 bg-orange-50 border border-orange-200 rounded">
                <div className="text-sm">
                  <span className="font-medium">Maximum Words: </span>
                  {essayMaxWords || 500} words
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {questionType === 'MATCHING' && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Matching Pairs Preview</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-2">
              {matchingPairs && matchingPairs.length > 0 ? (
                matchingPairs.map((pair) => (
                  <div key={pair.id} className="grid grid-cols-2 gap-4 p-3 bg-green-50 border border-green-200 rounded">
                    <div className="text-sm">
                      <span className="font-medium">Left: </span>
                      {pair.left || <span className="text-muted-foreground italic">Empty</span>}
                    </div>
                    <div className="text-sm">
                      <span className="font-medium">Right: </span>
                      {pair.right || <span className="text-muted-foreground italic">Empty</span>}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-muted-foreground italic text-sm">No matching pairs defined</div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Explanation Preview */}
      {explanation && (
        <KatexPreviewCard
          text={explanation}
          title="Explanation Preview"
          placeholder="Explanation preview will appear here..."
          portal="ADMIN"
          CardComponent={Card}
          CardHeaderComponent={CardHeader}
          CardContentComponent={CardContent}
          CardTitleComponent={CardTitle}
        />
      )}
    </div>
  );
};
